import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useAppStore } from "@/store/app-store";
import type { components } from "@/api/types";

/**
 * SOP-compliance data for the chief view — served by the typed
 * `/v1/sop-compliance/*` endpoints (RES-59/46). The scoring semantics
 * (NIOSH REL/ceiling, VOC screen, LEL action level, rehab worktime,
 * peak-no-dose integrity flag, roster synthesis and ordering) now live
 * SERVER-SIDE in one place, pinned by the backend pytest suite — the old
 * client-side `computeSopComplianceRollup` is gone by design so the page and
 * any future signed PDF can never disagree.
 *
 * Wire notes:
 * - Measured values (ppm, ppm·min, limits) arrive as Decimal-as-STRING; route
 *   them through `toNum` (@/lib/format) at render.
 * - Every hook keys on the active department and keeps the compliance
 *   freshness posture (staleTime 0 + refetch on focus): exposure records are
 *   written at incident close, so a just-closed call shows on page focus.
 * - No client fallback: a failed endpoint renders the page's error branch,
 *   never a silently recomputed client number.
 */

export type SopComplianceSummary = components["schemas"]["SopComplianceSummaryRead"];
export type SopLimits = components["schemas"]["SopLimitsRead"];
export type SopExceedances = components["schemas"]["SopExceedancesRead"];
export type SopExceedanceRow = components["schemas"]["SopExceedanceRowRead"];
export type SopFlaggedExceedance = components["schemas"]["SopFlaggedExceedanceRead"];
export type SopRecommendation = components["schemas"]["SopRecommendationRead"];
export type SopRosterMember = components["schemas"]["SopRosterMemberRead"];
export type SopMemberExceedance = components["schemas"]["SopMemberExceedanceRead"];
export type SopRuleResults = components["schemas"]["SopRuleResultsRead"];
export type SopRuleResult = components["schemas"]["SopRuleResultRead"];
export type SopRuleEvidence = components["schemas"]["SopRuleEvidenceRead"];

function useSopComplianceQuery<T>(
  section: string,
  path: string,
  departmentId: string | undefined,
) {
  return useQuery({
    queryKey: ["sop-compliance", section, departmentId],
    queryFn: () => api.get<T>(path),
    enabled: !!departmentId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useSopComplianceSummary(departmentId: string | undefined) {
  return useSopComplianceQuery<SopComplianceSummary>(
    "summary",
    "/sop-compliance/summary",
    departmentId,
  );
}

export function useSopExceedances(departmentId: string | undefined) {
  return useSopComplianceQuery<SopExceedances>(
    "exceedances",
    "/sop-compliance/exceedances",
    departmentId,
  );
}

export function useSopRecommendations(departmentId: string | undefined) {
  return useSopComplianceQuery<SopRecommendation[]>(
    "recommendations",
    "/sop-compliance/recommendations",
    departmentId,
  );
}

export function useSopRosterExposure(departmentId: string | undefined) {
  return useSopComplianceQuery<SopRosterMember[]>(
    "roster-exposure",
    "/sop-compliance/roster-exposure",
    departmentId,
  );
}

export function useSopRuleResults(departmentId: string | undefined) {
  return useSopComplianceQuery<SopRuleResults>(
    "rule-results",
    "/sop-compliance/rule-results",
    departmentId,
  );
}

// Tie the active-department selector into the hooks so the page need only call
// them. (Kept as a thin wrapper for symmetry with other dept-scoped pages.)
export function useActiveDepartmentId(
  fallback: string | undefined,
): string | undefined {
  const selectedDeptId = useAppStore((s) => s.selectedDepartmentId);
  return selectedDeptId ?? fallback;
}
